import { useId, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Banner } from '@/components/feedback/Banner'
import { Checkbox } from '@/components/ui/Checkbox'
import { Disclosure } from '@/components/ui/Disclosure'
import { DatePicker, Field, Select } from '@/components/ui/Field'
import { toOptions } from '@/components/ui/control'
import { FormSheet } from '@/components/ui/FormSheet'
import { Input } from '@/components/ui/Input'
import { Tag } from '@/components/ui/Tag'
import { useFormDraft } from '@/components/ui/useFormDraft'
import { useCase } from '@/features/cases/api/useCase'
import { refusalMessage } from '@/features/cases/lib/refusal'
import {
  useIssuingEntityOptions,
  usePayCarrier,
  type CarrierDebtDraft,
  type CarrierPaymentItemDraft,
  type CarrierPaymentPayload,
} from '@/features/finance/api/useFinance'
import {
  OFF_DIRECTORY,
  WHOLE_LEG,
  carrierOptions,
  payRows,
  type PayRow,
} from '@/features/finance/carrierPayRows'
import { CounterpartyPicker } from '@/features/counterparties/components/CounterpartyPicker'
import { COUNTERPARTY_CATEGORIES } from '@/features/counterparties/counterpartyList'
import { normaliseDecimal } from '@/features/finance/lines'
import { cn } from '@/lib/cn'
import { compareDecimal, decimalSum } from '@/lib/decimalSum'
import { formatMoney, formatMoneyString } from '@/lib/format'
import { UNIT_KINDS, unitKindTitle } from '@/lib/legUnits'
import { fieldErrorKey, fieldErrors } from '@/lib/problems'
import type { CaseLeg, LegMoney, PaymentMethod } from '@/types/domain'

/* «Оплата перевозчику» — one payment, several things it pays (S-18a).
 *
 * ── What it replaced ────────────────────────────────────────────────────────
 *
 * Marking "we paid 3 000 for truck X" used to take three writes — record the
 * ACTUAL cost line, record the payment, allocate the one to the other — and
 * then one sheet per truck sent all three as ONE request (spec L5). Three
 * trucks paid by one bank transfer were still three payments, and a sea or air
 * leg with no trucks could not be paid at all. This sheet sends one request
 * with ONE payment and a list of ITEMS: each is an existing actual the money
 * settles (`expenseLineId`) or a debt it recognises in the same breath
 * (`debt`), and each carries its own allocation. All of it, or none of it.
 *
 * ── The carrier first ───────────────────────────────────────────────────────
 *
 * Several items must all be ONE directory carrier's (`items:
 * carrier-mismatch`), so the sheet opens with «Перевозчик» and lists only that
 * carrier's rows grouped by kind — «Фуры», «Контейнеры», «Вагоны»,
 * «Авианакладные», «Коносаменты», «Другое» (S-18b) — then «Плечо целиком» when
 * the leg is theirs. Which carrier a row belongs to, and which single target it pays, are
 * `carrierPayRows.ts`'s answers — the server's own rules, so the sheet never
 * offers a set the server would refuse. A row with several invoices or rates is
 * refused with the reason; a paid one is greyed and tagged «оплачено»; under
 * «Перевозчик не из справочника» one row at a time is all a payment may carry.
 *
 * ── Every figure is served, except one ──────────────────────────────────────
 *
 * A row's amount defaults to its served remainder (an actual's `remaining`) or
 * its plan's own figure, and only while it is in the payment's currency —
 * across currencies the client holds no rates, so the box opens empty.
 * «Итого к оплате» is the one sum this client computes (owner decision), on the
 * strings through `decimalSum`, and it IS the payment amount sent; the server's
 * `sum-mismatch` is the backstop.
 *
 * ── The debt, where there is one ────────────────────────────────────────────
 *
 * A row that recognises a debt (a plan with no invoice, or nothing at all)
 * carries a folded «Долг»: the debt in its OWN currency (D1), opening at the
 * plan's figure while in the plan's currency (L3), and a reason REQUIRED above
 * the plan in one currency (L2) — across two the server compares booked USD and
 * its `debt.comment: required` lands under the field. An unplanned debt follows
 * the row's amount until somebody types one.
 *
 * ── Everybody named is a directory record (S-22d) ───────────────────────────
 *
 * The payee is PICKED from the directory, by id (`payment.counterpartyId`),
 * defaulting to the chosen carrier when that carrier is a directory row. A
 * debt is booked to the carrier the server's `carrierFor` picks, so a row
 * whose picked carrier is typed only cannot be ticked and says where to fix
 * it (`PayRow.carrierNotInDirectory`); the server's `items.<i>.debt:
 * carrier-not-in-directory` is the backstop, said on the row.
 *
 * ── One key per open ────────────────────────────────────────────────────────
 *
 * `clientKey` is generated when the sheet opens and sent with every press of
 * that open, so a retry after a lost answer replays the payment the first press
 * recorded (`200`) instead of booking a second one. A new open is a new act.
 *
 * ── What this sheet never states ────────────────────────────────────────────
 *
 * No booking (AD-9/AD-20), no direction, no case id and NO STATUS: a logist's
 * record is born `pending` and waits for Finance (L3).
 */

/** The server's own spellings, keyed to match `finance.payments.methods.*`. */
const METHODS: PaymentMethod[] = ['cash', 'bank_transfer']

const CURRENCIES = ['USD', 'EUR', 'CNY', 'RUB', 'TMT'] as const

/** Accepts `1234.56`, `1 234,56` and `1,234.56`; rejects text and blanks.
 *  Repeated rather than shared for the reason `PaymentAllocationSheet` gives:
 *  a component file may export only components. */
function isValidAmount(raw: string): boolean {
  const normalised = normaliseDecimal(raw)
  return normalised !== '' && /^\d+(\.\d{1,2})?$/.test(normalised)
}

/** A valid amount that is also above zero — what an item and a debt need. */
function isPositiveAmount(raw: string): boolean {
  return isValidAmount(raw) && compareDecimal(normaliseDecimal(raw), '0') === 1
}

/** A plain decimal printed at least to the cent — `'3700'` reads `3 700.00`,
 *  as every amount on these screens does. String padding, not arithmetic. */
function toCents(value: string): string {
  const [whole, fraction = ''] = value.split('.')
  return `${whole}.${fraction.padEnd(2, '0')}`
}

function today(): string {
  return new Date().toISOString().slice(0, 10)
}

/** A fresh idempotency key. `crypto.randomUUID` is everywhere this app runs
 *  (a secure context); the fallback keeps a test environment without it
 *  working, and is unique enough for one open of one sheet. */
function newClientKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `key-${Date.now()}-${Math.random().toString(36).slice(2)}`
}

/** One checked row's typed half. `null` means "follows": the debt's amount
 *  follows the plan's figure (or, unplanned, the row's own amount), and its
 *  currency follows the plan's (or the payment's). */
interface RowDraft {
  amount: string
  debtAmount: string | null
  debtCurrency: string | null
  debtComment: string
}

interface CarrierPaymentDraft {
  clientKey: string
  /** A directory participant's id, or `OFF_DIRECTORY`. */
  carrier: string
  /** The ticked rows, by key; their typed half is in `rows`. */
  checked: string[]
  rows: Record<string, RowDraft>
  currency: string
  reference: string
  /** `null` while the payee follows the chosen carrier — when that carrier is
   *  a directory row. A pick is a directory row (`id`) and its name. */
  payee: { id: string; name: string } | null
  method: PaymentMethod
  issuingEntityId: string
  feeAmount: string
  feeCurrency: string
  receivedOn: string
}

/** What a row's amount opens at in `currency`: its seed, only in its own. */
function seedIn(row: PayRow, currency: string): string {
  return row.seed && row.seed.currency === currency ? row.seed.amount : ''
}

function blankRow(row: PayRow, currency: string): RowDraft {
  return { amount: seedIn(row, currency), debtAmount: null, debtCurrency: null, debtComment: '' }
}

/** A row the reader can tick at all: one target, not paid, and — for a debt
 *  — a carrier the server will book it to (S-22d). */
function payable(row: PayRow): boolean {
  return row.target !== null && !row.paid && row.carrierNotInDirectory === undefined
}

/** The debt's effective currency and amount, with the "follows" defaults
 *  resolved. Only meaningful on a row whose target is a plan or new. */
function debtOf(row: PayRow, rowDraft: RowDraft, paymentCurrency: string) {
  const plan = row.target?.kind === 'plan' ? row.target.line : undefined
  const currency = rowDraft.debtCurrency ?? (plan ? plan.planned.currency : paymentCurrency)
  const amount =
    rowDraft.debtAmount ??
    (plan ? (currency === plan.planned.currency ? plan.planned.amount : '') : rowDraft.amount)
  return { plan, currency, amount }
}

export function CarrierPaymentSheet({
  open,
  onClose,
  caseId,
  leg,
  money,
  defaultUnitId,
  defaultWholeLeg,
}: {
  open: boolean
  onClose: () => void
  caseId: string
  /** The leg being paid: its units with their own carriers, and its carrier.
   *  Identity this reader may not see arrives withheld (AD-6), and the sheet
   *  lists such a row off the directory rather than as a name it cannot read. */
  leg: CaseLeg
  /** The leg's money block — the folded cost lines (with their served
   *  `remaining`), the per-unit and leg-level rollups. Passed in rather than
   *  read again: the caller is holding it, and a second read would be a second
   *  answer to what is owed. */
  money: LegMoney
  /** The unit the sheet opens on — its carrier chosen and its row ticked —
   *  when it was opened from that unit's row or from a cost line on it. */
  defaultUnitId?: string
  /** Opens on «Плечо целиком» instead — a leg-level cost line's drawer. */
  defaultWholeLeg?: boolean
}) {
  const { t } = useTranslation()
  /* Per sheet: the leg page mounts more than one (the summary's, a unit
     row's), and a closed sheet keeps its markup. */
  const whatId = useId()
  const { data: entities } = useIssuingEntityOptions()
  const pay = usePayCarrier()
  /* Read for one fact, the case's issuing entity, and only while the sheet is
     open: it is mounted closed on the leg page. */
  const { data: caseRecord } = useCase(caseId, { enabled: open })

  const rows = payRows({
    units: leg.units ?? [],
    expenses: money.expenses,
    perUnit: money.perUnit,
    legLevel: money.legLevel,
    legCarrierId: leg.carrierId,
    legCarrierName: leg.carrierName,
  })
  const options = carrierOptions(rows, { id: leg.carrierId, name: leg.carrierName })

  /* The refusal THIS open is answering, or null. The mutation outlives the
     sheet — it is mounted closed and serves every open — so a refusal left
     standing would greet the next open with errors about a draft that is gone.
     Remembered DURING render (the `useFormDraft` pattern), not reset in an
     effect, which would paint the stale refusal for a frame first. */
  const [wasOpen, setWasOpen] = useState(open)
  const [heldOver, setHeldOver] = useState<unknown>(null)
  /* The rows the last press sent, in item order: a served `items.<i>.…`
     names its row by that index. */
  const [sentKeys, setSentKeys] = useState<string[]>([])
  if (open !== wasOpen) {
    setWasOpen(open)
    if (open) setHeldOver(pay.error)
  }
  const failure = pay.error !== null && pay.error !== heldOver ? pay.error : null

  const openingKey = defaultWholeLeg ? WHOLE_LEG : (defaultUnitId ?? '')
  const [draft, setDraft, touched, markTouched, dirty] = useFormDraft<CarrierPaymentDraft>(
    open ? `${leg.id}:${openingKey}` : null,
    () => {
      /* Opened from a row: that row's carrier, that row ticked — when the row
         can be paid. A row refused only because its carrier is typed (S-22d)
         still opens on its own carrier, unticked, so the reason is shown
         beside the truck the reader pressed rather than another carrier's
         trucks. Otherwise the leg's own carrier when it is a directory
         participant, else the first directory carrier offered, and only then
         «Перевозчик не из справочника». */
      const opening = rows.find(
        (row) =>
          row.key === openingKey && (payable(row) || row.carrierNotInDirectory !== undefined),
      )
      const start = opening && payable(opening) ? opening : undefined
      const legKey = rows.find((row) => row.key === WHOLE_LEG)?.carrierKey
      const carrier =
        opening?.carrierKey ??
        (legKey !== undefined && legKey !== OFF_DIRECTORY
          ? options.find((option) => option.key === legKey)?.key
          : undefined) ??
        options.find((option) => option.key !== OFF_DIRECTORY)?.key ??
        OFF_DIRECTORY
      /* The payment's currency opens in the started row's own, else in the
         first payable row's of that carrier — the denomination the rate was
         agreed in, not a number anybody is inventing. */
      const currency =
        start?.seed?.currency ??
        rows.find((row) => row.carrierKey === carrier && payable(row) && row.seed)?.seed
          ?.currency ??
        'USD'
      return {
        clientKey: newClientKey(),
        carrier,
        checked: start ? [start.key] : [],
        rows: start ? { [start.key]: blankRow(start, currency) } : {},
        currency,
        reference: '',
        payee: null,
        method: 'bank_transfer' as PaymentMethod,
        issuingEntityId: '',
        feeAmount: '',
        // '' MEANS "the payment's own"; a code seeded here would pin the fee.
        feeCurrency: '',
        receivedOn: today(),
      }
    },
  )

  /* «Юрлицо» defaults to the entity the CASE is registered under (QW-8, P-1),
     DERIVED rather than written into the draft, so an untouched sheet stays
     clean and an id the list does not offer is never sent invisibly. */
  const caseEntityId = caseRecord?.entityId ?? ''
  const issuingEntityId =
    draft.issuingEntityId ||
    (caseEntityId !== '' && (entities ?? []).some((entity) => entity.id === caseEntityId)
      ? caseEntityId
      : '')

  const offDirectory = draft.carrier === OFF_DIRECTORY
  const ofCarrier = rows.filter((row) => row.carrierKey === draft.carrier)
  /* «Что оплачиваем», grouped by kind in the shared order (S-18b), then
     «Плечо целиком» — and `listed` IS that order, so the items a press sends
     follow what the reader sees. */
  const groups = UNIT_KINDS.map((kind) => ({
    kind,
    rows: ofCarrier.filter((row) => row.key !== WHOLE_LEG && row.kind === kind),
  })).filter((group) => group.rows.length > 0)
  const unitRows = groups.flatMap((group) => group.rows)
  const legRow = ofCarrier.find((row) => row.key === WHOLE_LEG)
  const listed = legRow ? [...unitRows, legRow] : unitRows
  /* Checked rows in the order they are listed — the item order. */
  const checkedRows = listed.filter((row) => draft.checked.includes(row.key) && payable(row))
  const rowDraftOf = (row: PayRow): RowDraft => draft.rows[row.key] ?? blankRow(row, draft.currency)

  /* The payee follows the chosen carrier until somebody picks one — when the
     carrier is a directory row, which it is everywhere but «Перевозчик не из
     справочника». There the ticked row's stored name, if any, is shown «как
     введено» and the press is refused until a directory pick (S-22d). */
  const chosenCarrier = offDirectory
    ? undefined
    : options.find((option) => option.key === draft.carrier)
  const payee = draft.payee ?? {
    id: chosenCarrier?.key ?? '',
    name: chosenCarrier?.name ?? checkedRows[0]?.carrierName ?? '',
  }

  /* The server's word, under the field it names. Item fields are matched to
     rows through the keys the press sent, in order. */
  const served = fieldErrors(failure)
  const word = (code: string | undefined) => (code ? t(fieldErrorKey(code)) : undefined)
  const itemError = (key: string, field: string): string | undefined => {
    const index = sentKeys.indexOf(key)
    if (index < 0) return undefined
    return word(served[field === '' ? `items.${index}` : `items.${index}.${field}`]?.[0])
  }
  /* A refusal of the target itself — the same unit twice, a plan no longer on
     the leg, a line not found, both a line and a debt — is said on the row. */
  const rowLevelError = (key: string) =>
    itemError(key, '') ??
    /* S-22d: a debt the server would book to a typed-only carrier. */
    itemError(key, 'debt') ??
    itemError(key, 'expenseLineId') ??
    itemError(key, 'debt.planLineId') ??
    itemError(key, 'debt.unitId') ??
    itemError(key, 'debt.wholeLeg')
  const totalServed = word(served.items?.[0]) ?? word(served['payment.amount']?.[0])

  /* Per-row local checks, while typing. */
  const rowProblems = (row: PayRow) => {
    const rowDraft = rowDraftOf(row)
    const amountBad = !isPositiveAmount(rowDraft.amount)
    if (row.target?.kind === 'actual') return { amountBad, debtBad: false, commentRequired: false, overPlan: false }
    const debt = debtOf(row, rowDraft, draft.currency)
    const debtBad = !isPositiveAmount(debt.amount)
    const overPlan =
      debt.plan !== undefined &&
      debt.currency === debt.plan.planned.currency &&
      !debtBad &&
      compareDecimal(normaliseDecimal(debt.amount), debt.plan.planned.amount) === 1
    return { amountBad, debtBad, commentRequired: overPlan && rowDraft.debtComment.trim() === '', overPlan }
  }

  const totalAmount = decimalSum(
    checkedRows.map((row) => normaliseDecimal(rowDraftOf(row).amount)),
  )

  const referenceError = touched && !draft.reference.trim() ? t('common.form.required') : undefined
  /* The payee's own codes (S-22d): none picked, or an id the directory does
     not hold — said in the cost line's words. `counterpartyName` is still the
     shared payment field the server may name. */
  const payeeServed = served['payment.counterpartyId']?.[0]
  const counterpartyError =
    (touched && !payee.id ? t('common.form.counterpartyPick') : undefined) ??
    (payeeServed === 'required'
      ? t('common.form.counterpartyPick')
      : payeeServed
        ? t('common.form.counterpartyUnknown')
        : undefined) ??
    word(served['payment.counterpartyName']?.[0])
  const entityError = touched && !issuingEntityId ? t('common.form.required') : undefined
  const feeTyped = draft.feeAmount.trim() !== ''
  const feeError =
    touched && feeTyped && !isValidAmount(draft.feeAmount) ? t('common.form.invalidNumber') : undefined

  const invalid =
    checkedRows.length === 0 ||
    checkedRows.some((row) => {
      const problem = rowProblems(row)
      return problem.amountBad || problem.debtBad || problem.commentRequired
    }) ||
    totalAmount === null ||
    !draft.reference.trim() ||
    !payee.id ||
    !issuingEntityId ||
    (feeTyped && !isValidAmount(draft.feeAmount))

  /* ── Edits ─────────────────────────────────────────────────────────────── */

  /* A different carrier is a different set of rows and a different payee:
     nothing ticked under the last one survives the change. */
  const chooseCarrier = (carrier: string) =>
    setDraft({ ...draft, carrier, checked: [], rows: {}, payee: null })

  const toggle = (row: PayRow, on: boolean) => {
    if (on) {
      setDraft({
        ...draft,
        checked: [...draft.checked, row.key],
        rows: { ...draft.rows, [row.key]: draft.rows[row.key] ?? blankRow(row, draft.currency) },
      })
      return
    }
    /* Unticked, its typed half goes too: the draft says what the screen says,
       so a tick and an untick is not an edit. */
    const { [row.key]: _dropped, ...rest } = draft.rows
    setDraft({ ...draft, checked: draft.checked.filter((key) => key !== row.key), rows: rest })
  }

  const patchRow = (row: PayRow, patch: Partial<RowDraft>) =>
    setDraft({ ...draft, rows: { ...draft.rows, [row.key]: { ...rowDraftOf(row), ...patch } } })

  /* A seeded amount is a figure in ONE currency: moved to another, an amount
     still equal to its seed is re-seeded in the new one (cleared when it has
     none there); a typed figure is the reader's and stays. */
  const chooseCurrency = (currency: string) => {
    const next: Record<string, RowDraft> = {}
    for (const [key, rowDraft] of Object.entries(draft.rows)) {
      const row = rows.find((candidate) => candidate.key === key)
      next[key] =
        row && rowDraft.amount === seedIn(row, draft.currency)
          ? { ...rowDraft, amount: seedIn(row, currency) }
          : rowDraft
    }
    setDraft({ ...draft, currency, rows: next })
  }

  /* ── The press ─────────────────────────────────────────────────────────── */

  const submit = () => {
    markTouched()
    if (invalid || totalAmount === null) return

    const items: CarrierPaymentItemDraft[] = checkedRows.map((row) => {
      const rowDraft = rowDraftOf(row)
      // A decimal STRING all the way through (AD-9), normalised only for the
      // separators a Russian keyboard produces — never through Number().
      const amount = normaliseDecimal(rowDraft.amount)
      if (row.target?.kind === 'actual') {
        return { amount, expenseLineId: row.target.line.actualId ?? '' }
      }
      const debt = debtOf(row, rowDraft, draft.currency)
      const comment = rowDraft.debtComment.trim()
      const debtDraft: CarrierDebtDraft = {
        /* With a plan, the plan's own unit (or none) is the attribution and
           the server ignores the rest; without one, exactly one of the unit
           and the whole leg says what the cost is for. */
        ...(debt.plan
          ? { planLineId: debt.plan.id }
          : row.unitId
            ? { unitId: row.unitId }
            : { wholeLeg: true as const }),
        amount: normaliseDecimal(debt.amount),
        currency: debt.currency,
        ...(comment === '' ? {} : { comment }),
      }
      return { amount, debt: debtDraft }
    })

    const payload: CarrierPaymentPayload = {
      clientKey: draft.clientKey,
      payment: {
        reference: draft.reference.trim(),
        /* The directory row, by id; the server writes ITS name into the
           payment, and the name here is what the reader saw. */
        counterpartyId: payee.id,
        counterpartyName: payee.name.trim(),
        method: draft.method,
        issuingEntityId,
        amount: { amount: totalAmount, currency: draft.currency },
        /* OMITTED when nothing was typed, never sent as a zero: absent means
           "no fee was recorded". */
        ...(feeTyped
          ? {
              fee: {
                amount: normaliseDecimal(draft.feeAmount),
                currency: draft.feeCurrency === '' ? draft.currency : draft.feeCurrency,
              },
            }
          : {}),
        receivedOn: draft.receivedOn,
      },
      items,
    }

    setSentKeys(checkedRows.map((row) => row.key))
    // Closed from onSuccess only: a refusal leaves the sheet open holding the
    // whole draft (UX-DR-18), and a retry of this open reuses its clientKey.
    pay.mutate({ caseId, legId: leg.id, payload }, { onSuccess: onClose })
  }

  /* ── Rendering ─────────────────────────────────────────────────────────── */

  const carrierSelectOptions = options.map((option) => ({
    value: option.key,
    label: option.key === OFF_DIRECTORY ? t('cases.legFinance.pay.offDirectory') : option.name || '—',
  }))

  /* The banner above the total: a refusal of the whole payment (a 409, the
     network, a 422 the rows cannot hold), or the local "fix the fields". */
  const banner = failure
    ? refusalMessage(failure, t)
    : touched && invalid && checkedRows.length > 0
      ? t('common.form.fixFields')
      : undefined

  const renderRow = (row: PayRow) => {
    const checked = draft.checked.includes(row.key) && payable(row)
    /* Off the directory one row at a time: the server cannot tell several
       name-only targets are one carrier's. The reason is printed above the
       list, so a bare `disabled` here says nothing twice. */
    const lockedOut = offDirectory && !checked && checkedRows.length > 0
    const label = row.key === WHOLE_LEG ? t('cases.legFinance.pay.wholeLeg') : row.label
    const rowDraft = rowDraftOf(row)
    const problem = checked ? rowProblems(row) : undefined
    const amountError =
      (touched && problem?.amountBad ? t('common.form.invalidNumber') : undefined) ??
      itemError(row.key, 'amount')
    const targetError = checked ? rowLevelError(row.key) : undefined
    const recognises = row.target?.kind === 'plan' || row.target?.kind === 'new'

    return (
      <li
        key={row.key}
        className={cn(
          'border-line-hair flex flex-col gap-2 border-b py-2.5 last:border-b-0',
          row.key === WHOLE_LEG && unitRows.length > 0 && 'border-line border-t',
        )}
      >
        <div className="grid grid-cols-[minmax(0,1fr)_auto_7.5rem] items-center gap-x-3">
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <Checkbox
              label={label}
              checked={checked}
              disabled={!payable(row) || lockedOut}
              onCheckedChange={(on) => toggle(row, on)}
            />
            {row.paid ? <Tag>{t('cases.legFinance.pay.paidTag')}</Tag> : null}
          </div>
          <span
            className={cn(
              'asa-num text-right text-[13px] whitespace-nowrap',
              row.paid ? 'text-text-muted' : 'text-text-body',
            )}
          >
            {row.remainder ? formatMoney(row.remainder.amount, row.remainder.currency) : '—'}
          </span>
          {checked ? (
            <Input
              inputSize="sm"
              inputMode="decimal"
              value={rowDraft.amount}
              onChange={(event) => patchRow(row, { amount: event.target.value })}
              aria-label={t('cases.legFinance.pay.rowAmount', { unit: label })}
              aria-invalid={amountError ? true : undefined}
              className="asa-num text-right"
            />
          ) : (
            <span aria-hidden className="bg-surface-sunken h-8 rounded-[var(--radius-DEFAULT)]" />
          )}
        </div>

        {row.target === null ? (
          <p className="text-text-muted text-[12px] leading-snug font-light">
            {t('cases.legFinance.pay.several')}
          </p>
        ) : null}
        {/* Printed on the row, so the checkbox's bare `disabled` says nothing
            twice — and it names where the carrier is fixed. */}
        {row.carrierNotInDirectory ? (
          <p className="text-text-muted text-[12px] leading-snug font-light">
            {t(
              row.carrierNotInDirectory === 'unit'
                ? 'cases.legFinance.pay.unitCarrierNotInDirectory'
                : 'cases.legFinance.pay.legCarrierNotInDirectory',
            )}
          </p>
        ) : null}
        {amountError ? (
          <p className="text-negative text-right text-[12px] leading-snug">{amountError}</p>
        ) : null}
        {targetError ? (
          <p className="text-negative text-[12px] leading-snug">{targetError}</p>
        ) : null}

        {checked && recognises && problem ? (
          <DebtFields
            row={row}
            rowDraft={rowDraft}
            paymentCurrency={draft.currency}
            touched={touched}
            problem={problem}
            servedError={(field) => itemError(row.key, `debt.${field}`)}
            onChange={(patch) => patchRow(row, patch)}
          />
        ) : null}
      </li>
    )
  }

  return (
    <FormSheet
      open={open}
      onClose={onClose}
      title={t('cases.legFinance.pay.title')}
      submitLabel={t('cases.legFinance.pay.submit')}
      pending={pay.isPending}
      dirty={dirty}
      blockedReason={checkedRows.length === 0 ? t('cases.legFinance.pay.chooseRows') : undefined}
      notice={t('cases.legFinance.pay.atomicNote')}
      onSubmit={submit}
    >
      <div className="flex gap-2.5">
        <Field label={t('cases.legFinance.pay.carrier')} className="min-w-0 flex-1">
          {(props) => (
            <Select
              value={draft.carrier}
              onChange={chooseCarrier}
              options={carrierSelectOptions}
              {...props}
            />
          )}
        </Field>
        <Field label={t('cases.legFinance.pay.currency')} className="w-28 shrink-0">
          {(props) => (
            <Select
              value={draft.currency}
              onChange={chooseCurrency}
              options={toOptions(CURRENCIES, (currency) => currency)}
              {...props}
            />
          )}
        </Field>
      </div>

      <section aria-labelledby={whatId} className="flex flex-col gap-1.5">
        <h3 id={whatId} className="asa-eyebrow">
          {t('cases.legFinance.pay.whatHeading')}
        </h3>
        {offDirectory ? (
          <p className="text-text-muted text-[12px] leading-snug font-light">
            {t('cases.legFinance.pay.offDirectoryOne')}
          </p>
        ) : null}

        {listed.length === 0 ? (
          <p className="text-text-muted text-[13px] leading-snug">{t('cases.legFinance.pay.noRows')}</p>
        ) : (
          <>
            {/* The column heads: plain caps on a hairline — no grey fill. */}
            <div
              aria-hidden
              className="border-line text-text-muted grid grid-cols-[minmax(0,1fr)_auto_7.5rem] gap-x-3 border-b pb-1.5 text-[10px] font-medium tracking-wide uppercase"
            >
              <span className="pl-6">{t('cases.legFinance.pay.columns.unit')}</span>
              <span className="text-right">{t('cases.legFinance.pay.columns.remainder')}</span>
              <span className="text-right">{t('cases.legFinance.pay.columns.amount')}</span>
            </div>
            {groups.map((group) => (
              <div key={group.kind} className="flex flex-col">
                <span className="text-text-muted pt-2 text-[12.5px]">{unitKindTitle(t, group.kind)}</span>
                <ul aria-label={unitKindTitle(t, group.kind)} className="flex flex-col">
                  {group.rows.map(renderRow)}
                </ul>
              </div>
            ))}
            {legRow ? <ul className="flex flex-col">{renderRow(legRow)}</ul> : null}
          </>
        )}
      </section>

      {banner ? (
        <div role="alert">
          <Banner tone="negative" title={banner} />
        </div>
      ) : null}

      <div className="border-line flex flex-col gap-1 border-t pt-3">
        <div className="flex items-baseline justify-between gap-3">
          <span className="text-text-heading text-[16px] leading-tight font-normal">
            {t('cases.legFinance.pay.total')}
          </span>
          <span
            data-testid="carrier-pay-total"
            className="asa-num text-text-heading text-[18px] leading-tight font-semibold whitespace-nowrap"
          >
            {totalAmount === null ? '—' : formatMoneyString(toCents(totalAmount), draft.currency)}
          </span>
        </div>
        {totalServed ? (
          <p className="text-negative text-[12px] leading-snug">{totalServed}</p>
        ) : null}
      </div>

      <div className="border-line-hair flex flex-col gap-2.5 border-t pt-3">
        <span className="asa-eyebrow">{t('cases.legFinance.pay.paymentHeading')}</span>

        <div className="flex gap-2.5">
          {/* Money cannot leave in the future: a date past today is a typo. */}
          <Field label={t('finance.payments.received')} required className="flex-1">
            {(props) => (
              <DatePicker
                value={draft.receivedOn}
                max={today()}
                onChange={(receivedOn) => setDraft({ ...draft, receivedOn })}
                {...props}
              />
            )}
          </Field>
          {/* Cash or bank: they reconcile against different evidence. */}
          <Field label={t('finance.payments.method')} required className="flex-1">
            {(props) => (
              <Select
                value={draft.method}
                onChange={(method) => setDraft({ ...draft, method })}
                options={toOptions(METHODS, (method) => t(`finance.payments.methods.${method}`))}
                {...props}
              />
            )}
          </Field>
        </div>

        <div className="flex gap-2.5">
          <Field
            label={t('finance.payments.fee')}
            error={feeError}
            hint={t('finance.payments.feeHint')}
            className="flex-1"
          >
            {(props) => (
              <Input
                inputMode="decimal"
                value={draft.feeAmount}
                onChange={(e) => setDraft({ ...draft, feeAmount: e.target.value })}
                className="asa-num"
                {...props}
              />
            )}
          </Field>
          <Field label={t('finance.payments.feeCurrency')} className="w-28 shrink-0">
            {(props) => (
              <Select
                value={draft.feeCurrency === '' ? draft.currency : draft.feeCurrency}
                onChange={(feeCurrency) => setDraft({ ...draft, feeCurrency })}
                options={toOptions(CURRENCIES, (currency) => currency)}
                {...props}
              />
            )}
          </Field>
        </div>

        <Field
          label={t('finance.payments.reference')}
          required
          error={referenceError}
          hint={t('finance.payments.referenceHint')}
        >
          {(props) => (
            <Input
              value={draft.reference}
              onChange={(e) => setDraft({ ...draft, reference: e.target.value })}
              className="font-mono"
              {...props}
            />
          )}
        </Field>

        {/* PICKED from the directory, by id (S-22d): every counterparty the
            company pays is one record. All six categories — a payee is
            whoever the money went to, a broker or an agent as often as a
            haulier. Prefilled with the chosen carrier when it is one. */}
        <Field
          label={t('cases.legFinance.pay.payee')}
          required
          error={counterpartyError}
          hint={t('cases.legFinance.pay.payeeHint')}
        >
          {(props) => (
            <CounterpartyPicker
              value={payee.id}
              name={payee.name}
              onChange={(next) => setDraft({ ...draft, payee: next })}
              categories={COUNTERPARTY_CATEGORIES}
              strings={{
                placeholder: t('cases.legFinance.pay.payeePlaceholder'),
                search: t('finance.payments.counterpartySearch'),
                asEntered: t('finance.payments.counterpartyAsEntered'),
                inactive: t('cases.legs.carrierInactive'),
                add: t('finance.payments.counterpartyAdd'),
              }}
              {...props}
            />
          )}
        </Field>

        {/* OURS, not theirs: which of the company's entities the money left,
            which is what netting is computed per (FR-35). */}
        <Field label={t('finance.payments.entity')} required error={entityError}>
          {(props) => (
            <Select
              value={issuingEntityId}
              onChange={(id) => setDraft({ ...draft, issuingEntityId: id })}
              options={(entities ?? []).map((entity) => ({ value: entity.id, label: entity.name }))}
              placeholder={t('common.form.selectPlaceholder')}
              {...props}
            />
          )}
        </Field>
      </div>
    </FormSheet>
  )
}

/** A checked row's «Долг» — folded, because the debt is usually the rate. It
 *  opens by itself when something inside it is refused, so the reason is
 *  never behind a press. */
function DebtFields({
  row,
  rowDraft,
  paymentCurrency,
  touched,
  problem,
  servedError,
  onChange,
}: {
  row: PayRow
  rowDraft: RowDraft
  paymentCurrency: string
  touched: boolean
  problem: { debtBad: boolean; commentRequired: boolean; overPlan: boolean }
  servedError: (field: 'amount' | 'currency' | 'comment') => string | undefined
  onChange: (patch: Partial<RowDraft>) => void
}) {
  const { t } = useTranslation()
  const debt = debtOf(row, rowDraft, paymentCurrency)
  const plan = debt.plan

  const amountError =
    (touched && problem.debtBad ? t('common.form.invalidNumber') : undefined) ?? servedError('amount')
  const currencyError = servedError('currency')
  const commentError =
    (touched && problem.commentRequired ? t('common.form.required') : undefined) ??
    servedError('comment')
  /* Opened by the FIRST refusal inside it, and left to the reader after: a
     latch rather than the live error, or the block would fold shut under the
     cursor the moment the first typed letter answered the refusal. */
  const refused = amountError !== undefined || currencyError !== undefined || commentError !== undefined
  const [opened, setOpened] = useState(false)
  if (refused && !opened) setOpened(true)

  /* The offered codes, plus the plan's when it is not among them: a picker
     never drops a stored value it does not recognise. */
  const currencyOptions = toOptions(
    plan && !(CURRENCIES as readonly string[]).includes(plan.planned.currency)
      ? [...CURRENCIES, plan.planned.currency]
      : [...CURRENCIES],
    (currency) => currency,
  )
  const planFigure = plan ? formatMoney(plan.planned.amount, plan.planned.currency) : ''

  return (
    <Disclosure
      /* Remounted open when the first refusal lands inside it. */
      key={opened ? 'refused' : 'quiet'}
      defaultOpen={opened}
      summary={t('cases.legFinance.pay.debtSummary', {
        amount: isValidAmount(debt.amount)
          ? formatMoneyString(normaliseDecimal(debt.amount), debt.currency)
          : '—',
      })}
      count={plan ? 3 : 2}
      bodyClassName="flex flex-col gap-2.5"
    >
      <div className="flex gap-2.5">
        <Field
          label={t('cases.legFinance.pay.debtAmount')}
          required
          error={amountError}
          hint={
            plan
              ? debt.currency === plan.planned.currency
                ? t('cases.legFinance.pay.debtFromPlan', { amount: planFigure })
                : t('cases.legFinance.pay.debtOtherCurrency', {
                    amount: planFigure,
                    currency: debt.currency,
                  })
              : t('cases.legFinance.pay.debtUnplanned')
          }
          className="flex-1"
        >
          {(props) => (
            <Input
              inputMode="decimal"
              value={debt.amount}
              onChange={(event) => onChange({ debtAmount: event.target.value })}
              className="asa-num"
              {...props}
            />
          )}
        </Field>
        <Field label={t('cases.legFinance.pay.debtCurrency')} error={currencyError} className="w-28 shrink-0">
          {(props) => (
            <Select
              value={debt.currency}
              onChange={(code) => onChange({ debtCurrency: code })}
              options={currencyOptions}
              {...props}
            />
          )}
        </Field>
      </div>

      {/* Two currencies, two days' rates (L3, R2) — said before the save. */}
      {debt.currency !== paymentCurrency ? (
        <p className="text-text-muted text-[12px] leading-snug font-light">
          {t('cases.legFinance.pay.debtPaymentCurrencies')}
        </p>
      ) : null}

      {/* Required only ABOVE the plan (L2). It lands in `remarks`, which AD-6
          suppresses — a reason may name a counterparty. */}
      {plan ? (
        <Field
          label={t('cases.legFinance.pay.debtComment')}
          required={problem.overPlan}
          error={commentError}
          hint={
            problem.overPlan
              ? t('cases.legFinance.pay.debtCommentRequired', { amount: planFigure })
              : debt.currency === plan.planned.currency
                ? t('cases.legFinance.pay.debtCommentHint')
                : t('cases.legFinance.pay.debtCommentInUsd', { amount: planFigure })
          }
        >
          {(props) => (
            <Input
              value={rowDraft.debtComment}
              onChange={(event) => onChange({ debtComment: event.target.value })}
              {...props}
            />
          )}
        </Field>
      ) : null}
    </Disclosure>
  )
}
